import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DocumentGallery } from '../components/DocumentGallery';
import { DocumentLoadStatus } from '../components/DocumentLoadStatus';
import { ErrorBox, Loading } from '../components/Feedback';
import { SignaturePad } from '../components/SignaturePad';
import { TicketHeader } from '../components/TicketHeader';
import { api, ticketRunId } from '../lib/api';
import { mergeTicketPayload } from '../lib/ticket-payload';
import { useTickets } from '../lib/ticket-state';
import { useTicketDocuments } from '../lib/use-ticket-documents';
import { activityEligible, can, isStockReview, stockReviewReportError } from '../lib/workflow';
import type { Activity, PartReplacementLine, Ticket } from '../types';

interface ReportDraft {
  activityIds?: string[];
  observations?: string;
  clientLastName?: string;
  clientFirstName?: string;
  clientSignature?: string;
}

function draftKey(id: string, runId: string) { return `nova:intervention:v3:${id}:${runId}`; }
function partsKey(id: string, runId: string) { return `nova:parts:${id}:${runId}`; }
function replacementsKey(id: string, runId: string) { return `nova:replacements:${id}:${runId}`; }

export function ClosePage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const routeRunId = params.get('runId') || '';
  const navigate = useNavigate();
  const { upsert } = useTickets();
  const saved = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(draftKey(id, routeRunId)) || '{}') as ReportDraft; }
    catch { return {}; }
  }, [id, routeRunId]);
  const [ticket, setTicket] = useState<Ticket>();
  const [catalog, setCatalog] = useState<Activity[]>([]);
  const { documents, documentsReady, documentsLoading, documentsError, reloadDocuments } = useTicketDocuments(id, ticketRunId(ticket || {}, routeRunId), Boolean(ticket));
  const [selected, setSelected] = useState<string[]>(saved.activityIds || []);
  const [observations, setObservations] = useState(saved.observations || '');
  const [clientLastName, setClientLastName] = useState(saved.clientLastName || '');
  const [clientFirstName, setClientFirstName] = useState(saved.clientFirstName || '');
  const [clientSignature, setClientSignature] = useState(saved.clientSignature || '');
  const [confirmedWithoutRemoved, setConfirmedWithoutRemoved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const replacements = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(replacementsKey(id, routeRunId)) || '[]') as PartReplacementLine[]; }
    catch { return []; }
  }, [id, routeRunId]);

  useEffect(() => {
    void Promise.all([
      api.ticket(id, routeRunId), api.intervention(id, routeRunId), api.activities(),
    ]).then(([detail, intervention, activities]) => {
      const next = mergeTicketPayload(detail, intervention, routeRunId);
      setTicket(next);
      upsert(next);
      setCatalog(activities
        .map((item) => ({ ...item, id: item.id || String((item as unknown as { _id?: string })._id || '') }))
        .filter((activity) => isStockReview(next) ? activityEligible(activity, next) : !activity.ticketTypes?.length || activity.ticketTypes.includes(String(next.type || ''))));
    }).catch((reason: unknown) => setError(reason instanceof Error ? reason.message : 'Raportul nu poate fi încărcat.'));
  }, [id, routeRunId, upsert]);

  useEffect(() => {
    const timer = window.setTimeout(() => localStorage.setItem(draftKey(id, routeRunId), JSON.stringify({
      activityIds: selected,
      observations,
      clientLastName,
      clientFirstName,
      clientSignature,
      updatedAt: new Date().toISOString(),
    })), 300);
    return () => clearTimeout(timer);
  }, [clientFirstName, clientLastName, clientSignature, id, observations, routeRunId, selected]);

  const validReplacements = replacements.filter((line) => line.installed || line.removed);
  const installedWithoutRemoved = validReplacements.filter((line) => line.installed && !line.removed);

  async function finalize() {
    if (busy) return;
    if (!documentsReady && Number(ticket?.intervention?.requiredPhotos ?? ticket?.requiredPhotos ?? 0) > 0) {
      setError('Lista fotografiilor nu este verificată. Reîncearcă citirea listei înainte de finalizare.');
      return;
    }
    if (!ticket || !selected.length) {
      setError('Selectează cel puțin o activitate efectuată.');
      return;
    }
    if (isStockReview(ticket) && !can(ticket, 'finalize')) {
      setError('Finalizarea reviziei nu este disponibilă în această etapă.');
      return;
    }
    // Validate the review report before any stock movements are submitted.
    const reportError = stockReviewReportError(ticket, observations, selected, catalog);
    if (reportError) { setError(reportError); return; }
    if (installedWithoutRemoved.length && !confirmedWithoutRemoved) {
      setError('Confirmă explicit că piesele montate indicate nu au avut piese defecte demontate.');
      return;
    }
    const required = Number(ticket.intervention?.requiredPhotos ?? ticket.requiredPhotos ?? 0);
    const photoCount = documents.filter((item) => String(item.mimeType || '').startsWith('image/')).length;
    if (photoCount < required) {
      setError(`Sunt necesare minimum ${required} fotografii.`);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const activeRunId = ticketRunId(ticket, routeRunId);
      if (validReplacements.length) await api.replacements(ticket, validReplacements, activeRunId);
      const stockReview = isStockReview(ticket);
      const result = await api.finalize(id, ticket, selected, observations.trim(), stockReview ? '' : clientSignature, activeRunId, stockReview ? '' : clientLastName.trim(), stockReview ? '' : clientFirstName.trim());
      upsert({ ...ticket, ...result } as Ticket);
      localStorage.removeItem(draftKey(id, routeRunId));
      localStorage.removeItem(partsKey(id, routeRunId));
      localStorage.removeItem(replacementsKey(id, routeRunId));
      sessionStorage.setItem('nova:tickets:active-tab', 'completed');
      navigate('/tickets?tab=completed', { replace: true });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Tichetul nu a putut fi finalizat.');
    } finally {
      setBusy(false);
    }
  }

  if (!ticket) return error ? <ErrorBox message={error} /> : <Loading />;
  return <div className="page">
    <TicketHeader ticket={ticket} title="Raport final" />
    <section className="page-body with-cta">
      <article className="card"><h2>{ticket.deviceName}</h2><p>{ticket.clientName} · {ticket.locationName}</p></article>
      <section>
        <span className="eyebrow">Activități efectuate *</span>
        <div className="chips">{catalog.map((activity) => {
          const active = selected.includes(activity.id);
          return <button type="button" key={activity.id || activity.name} className={active ? 'active' : ''} onClick={() => setSelected((items) => active ? items.filter((value) => value !== activity.id) : [...items, activity.id])}>{active ? '✓ ' : ''}{activity.name}</button>;
        })}</div>
        {!catalog.length && <div className="alert">Catalogul de activități este indisponibil.</div>}
      </section>
      {validReplacements.length > 0 && <section><span className="eyebrow">Piese montate și demontate</span><div className="summary-list">{validReplacements.flatMap((line) => [line.installed ? <div key={`${line.replacementId}:installed`}><span>Montată: {line.installed.partCode} · {line.installed.partName}</span><strong>{line.installed.qty} buc.</strong></div> : null, line.removed ? <div key={`${line.replacementId}:removed`}><span>Defectă: {line.removed.partCode} · {line.removed.partName}</span><strong>{line.removed.qty} buc.</strong></div> : null])}</div></section>}
      {installedWithoutRemoved.length > 0 && <label className="replacement-confirmation"><input type="checkbox" checked={confirmedWithoutRemoved} onChange={(event) => { setConfirmedWithoutRemoved(event.target.checked); if (event.target.checked) setError(''); }} /><span>Confirm că pentru {installedWithoutRemoved.length === 1 ? 'piesa montată' : 'piesele montate'} nu a fost demontată nicio piesă defectă.</span></label>}
      <section><span className="eyebrow">Documente și fotografii ({documentsReady ? documents.length : '—'})</span><DocumentLoadStatus loading={documentsLoading} error={documentsError} onRetry={() => void reloadDocuments()} /><DocumentGallery documents={documents} /></section>
      <label className="observations"><span>Observații{isStockReview(ticket) ? ' *' : ''}</span><textarea rows={6} required={isStockReview(ticket)} maxLength={isStockReview(ticket) ? 10_000 : undefined} placeholder="Descrie clar intervenția, rezultatul și eventualele recomandări…" value={observations} onChange={(event) => setObservations(event.target.value)} /></label>
      {!isStockReview(ticket) && <><div className="client-identity-fields" aria-label="Identificare client semnatar">
        <label><span>Nume client (opțional)</span><input className="input" type="text" maxLength={120} autoComplete="family-name" value={clientLastName} onChange={(event) => setClientLastName(event.target.value)} /></label>
        <label><span>Prenume client (opțional)</span><input className="input" type="text" maxLength={120} autoComplete="given-name" value={clientFirstName} onChange={(event) => setClientFirstName(event.target.value)} /></label>
      </div>
      <SignaturePad value={clientSignature} onChange={setClientSignature} />
      <p className="muted">Ciorna, inclusiv identitatea și semnătura clientului, se salvează automat pe acest telefon. Raportul va conține activitățile, observațiile, piesele și datele clientului semnatar.</p></>}
      {error && <ErrorBox message={error} />}
    </section>
    <div className="cta"><button className="btn btn-primary block" disabled={busy || (isStockReview(ticket) && !can(ticket, 'finalize')) || (!documentsReady && Number(ticket.intervention?.requiredPhotos ?? ticket.requiredPhotos ?? 0) > 0) || !selected.length} onClick={() => void finalize()}>{busy ? 'Se finalizează…' : 'Finalizează tichetul'}</button></div>
  </div>;
}
